import { Box, Card, CardContent, Typography } from "@material-ui/core";
import classnames from "classnames";
import { PropsWithChildren, useCallback, useMemo } from "react";
import { Button, useNotify, useTranslate } from "react-admin";
import {
  FormProvider,
  SubmitHandler,
  useForm,
  useFormContext,
} from "react-hook-form";
import { CompactFormToolbar } from "../../../../../common/form/CompactFormToolbar";
import { CompactSaveButton } from "../../../../../common/form/CompactSaveButton";
import { useAxiosInstance } from "../../../../../../common/hooks/useAxiosInstance";
import { EventApiFactory } from "../../../../../../common/data/service-proxies";
import { handleAxiosError } from "../../../../../../common/data/axiosErrorHandler";
import { Check, Clear, ClearAll, Close } from "@material-ui/icons";
import { HookFormVehiclePicker } from "../../../../../common/form/hook-form/inputs/HookFormVehiclePicker";

interface Props {
  selectedRows: number[];
  currentVehicles?: Array<number | null | undefined>;
  latestEvent?: number;
  onSuccess: () => void;
  onFailed?: () => void;
}

type FormValues = {
  vehicleId: number | null;
};

const DefaultCompactFormToolbar = ({ children }: PropsWithChildren<any>) => {
  const {
    formState: { isSubmitting, isValid },
  } = useFormContext();
  return (
    <CompactFormToolbar>
      <CompactSaveButton
        saving={isSubmitting}
        disabled={isSubmitting || !isValid}
      />
      {children}
    </CompactFormToolbar>
  );
};

export const ChangeSelectedEventsVehicleForm = ({
  selectedRows,
  currentVehicles,
  latestEvent,
  onSuccess,
  onFailed,
  ...rest
}: Props) => {
  const notify = useNotify();
  const translate = useTranslate();
  const axios = useAxiosInstance();
  const selectedVehicles = useMemo(
    () => Array.from(new Set(currentVehicles?.filter((x) => x)).values()),
    [currentVehicles]
  );

  const onSubmit = useCallback(
    (data: FormValues) => {
      if (!axios) {
        return;
      }

      return EventApiFactory(undefined, axios.defaults.baseURL, axios)
        .apiAppEventBulksetvehiclePost({
          selectedEvents: selectedRows,
          vehicleId: data.vehicleId,
          isLatestEventSelected:
            !!latestEvent && selectedRows.includes(latestEvent),
        })
        .then(() => {
          onSuccess();
        })
        .catch((e) => {
          if (onFailed) {
            onFailed();
          }

          console.error(e);
          notify(handleAxiosError(e), { type: "error" });
        });
    },
    [axios, selectedRows, latestEvent, notify, onSuccess, onFailed]
  );

  const formContext = useForm<FormValues>({
    mode: "onChange",
  });

  const { handleSubmit, control, watch, setValue } = formContext;
  const vehicleId = watch("vehicleId");

  const handleSubmitInternal: SubmitHandler<FormValues> = (data) => {
    return Promise.resolve(onSubmit(data)).catch((e: Error) => {
      notify(e.message ?? "An unexpected error has occurred", "error");
    });
  };

  return (
    <CardContent>
      <FormProvider {...formContext}>
        <form
          className={classnames("simple-form", "form-create-edit-drawer")}
          {...sanitizeRestProps(rest)}
          onSubmit={handleSubmit(handleSubmitInternal, (err) =>
            console.error(err)
          )}>
          <Card>
            <CardContent>
              <div className="ra-input">
                <HookFormVehiclePicker<FormValues>
                  control={control}
                  name="vehicleId"
                  label="Vehicle"
                  variant="outlined"
                  rules={{ required: translate("ra.validation.required") }}
                  defaultValue={
                    selectedVehicles.length === 1 ? selectedVehicles[0] : null
                  }
                />
              </div>
              <div className="ra-input">
                <Box display="flex" alignItems="center" gridGap={5}>
                  {!!latestEvent && selectedRows.includes(latestEvent) ? (
                    <Check fontSize="small" />
                  ) : (
                    <Clear fontSize="small" />
                  )}
                  <Typography variant="body2">latest event selected</Typography>
                </Box>
              </div>
            </CardContent>
          </Card>
          <DefaultCompactFormToolbar>
            {!!vehicleId ? (
              <Button label="Clear" onClick={() => setValue("vehicleId", null)}>
                <ClearAll />
              </Button>
            ) : null}
            <Button label="Leave unchanged" onClick={onFailed}>
              <Close />
            </Button>
          </DefaultCompactFormToolbar>
        </form>
      </FormProvider>
    </CardContent>
  );
};

const sanitizeRestProps = ({
  active,
  dirty,
  dirtyFields,
  dirtyFieldsSinceLastSubmit,
  dirtySinceLastSubmit,
  error,
  errors,
  form,
  hasSubmitErrors,
  hasValidationErrors,
  initialValues,
  modified = null,
  modifiedSinceLastSubmit,
  save = null,
  submitError,
  submitErrors,
  submitFailed,
  submitSucceeded,
  submitting,
  touched = null,
  valid,
  values,
  visited = null,
  __versions = null,
  ...props
}: any) => props;
